import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Loader2, Filter, AlertTriangle, CheckCircle, Target, Sparkles, HelpCircle, PlayCircle, TrendingUp } from "lucide-react";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../lib/auth";

const STAGES = [
  { key: "alcance", label: "Alcance", color: "#FF7A5A" },
  { key: "cliquesBio", label: "Cliques na bio", color: "#FF9B5A" },
  { key: "acessoPagina", label: "Acesso à página", color: "#FF4E9E" },
  { key: "leads", label: "Leads", color: "#D856DE" },
  { key: "mql", label: "MQL", color: "#C05BFF" },
  { key: "vendas", label: "Vendas", color: "#8B5CF6" },
];

const OBJETIVOS = [
  { key: "awareness", label: "Awareness — Alcance & Reconhecimento", hint: "Foco em quantas pessoas viram sua marca." },
  { key: "autoridade", label: "Autoridade — Educar & ganhar credibilidade", hint: "Foco em conteúdo denso, salvamentos e retorno." },
  { key: "leads_mql", label: "Leads & MQL — Gerar demanda qualificada", hint: "Foco em transformar tráfego em contato qualificado." },
  { key: "comunidade", label: "Comunidade — Engajamento & retenção", hint: "Foco em interação recorrente e público fiel." },
  { key: "vendas", label: "Vendas — Conversão direta", hint: "Foco em MQL→Vendas e ticket." },
];

const RATE_LABELS = {
  cliquesBio_over_alcance: "CTR (Alcance → Cliques)",
  acessoPagina_over_cliquesBio: "Cliques → Página",
  leads_over_acessoPagina: "Página → Leads",
  mql_over_leads: "Leads → MQL",
  vendas_over_mql: "MQL → Vendas",
};

const EXAMPLE_DATA = {
  vol: { alcance: 42000, cliquesBio: 1250, acessoPagina: 780, leads: 92, mql: 38, vendas: 9 },
  meta: { alcance: 60000, cliquesBio: 2000, acessoPagina: 1200, leads: 150, mql: 60, vendas: 15 },
  dono: { alcance: "Social", cliquesBio: "Social", acessoPagina: "Web", leads: "Marketing", mql: "SDR", vendas: "Comercial" },
};

function pct(num, den) {
  if (!den || den <= 0) return null;
  return (num / den) * 100;
}
function fmtRate(v) {
  if (v === null || v === undefined || !isFinite(v)) return "—";
  return `${v.toFixed(1)}%`;
}

function InlineNum({ value, onChange, testid }) {
  return (
    <input
      type="number"
      className="input py-1.5 text-sm w-full"
      value={value ?? 0}
      onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
      data-testid={testid}
    />
  );
}

function InlineText({ value, onChange, placeholder, testid }) {
  return (
    <input
      className="input py-1.5 text-sm w-full"
      value={value || ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
    />
  );
}

function DiagnosticCard({ diagnostic, objetivoLabel }) {
  if (!diagnostic) return null;
  const d = diagnostic.diagnostic || {};
  if (d.error || !d.gargalo) {
    return (
      <div className="card p-5" data-testid="diag-raw">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={16} className="text-[var(--warning)]" />
          <span className="text-sm font-semibold">Resposta bruta da IA</span>
        </div>
        <div className="text-xs text-muted mb-2">{d.error}</div>
        <pre className="text-xs whitespace-pre-wrap card-2 p-3 max-h-96 overflow-auto">{d.raw || JSON.stringify(d, null, 2)}</pre>
      </div>
    );
  }
  return (
    <div className="grid gap-4 md:grid-cols-3" data-testid="diagnostic-structured">
      {/* Gargalo */}
      <div className="card p-5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10" style={{ background: "linear-gradient(120deg, #f87171 0%, #FF4E9E 100%)" }} />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={14} className="text-[var(--warning)]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted">// gargalo</span>
          </div>
          <div className="text-lg font-extrabold mb-1" data-testid="diag-gargalo-etapa">{d.gargalo.etapa}</div>
          <p className="text-sm text-muted leading-relaxed" data-testid="diag-gargalo-porque">{d.gargalo.por_que}</p>
        </div>
      </div>
      {/* Leitura vs Objetivo */}
      <div className="card p-5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10" style={{ background: "var(--grad-soft)" }} />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <Target size={14} className="grad-text" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted">// leitura vs objetivo</span>
          </div>
          <div className="text-xs grad-text font-bold mb-2 uppercase tracking-wide">{objetivoLabel}</div>
          <p className="text-sm leading-relaxed" data-testid="diag-leitura">{d.leitura_vs_objetivo}</p>
        </div>
      </div>
      {/* Recomendações */}
      <div className="card p-5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10" style={{ background: "linear-gradient(120deg, #4ade80 0%, #C05BFF 100%)" }} />
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={14} className="text-[var(--success)]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted">// 3 recomendações</span>
          </div>
          <ol className="space-y-3" data-testid="diag-recomendacoes">
            {(d.recomendacoes || []).slice(0, 3).map((r, i) => (
              <li key={i} className="text-sm" data-testid={`diag-rec-${i}`}>
                <div className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full grad-bg text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                  <div className="flex-1">
                    <div className="font-bold">{r.titulo}</div>
                    <div className="text-xs text-muted mt-0.5">{r.acao}</div>
                    {r.impacto_esperado && <div className="text-[11px] mt-1 chip inline-block" style={{ padding: "2px 6px" }}>↑ {r.impacto_esperado}</div>}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onLoadExample }) {
  return (
    <div className="card p-8 text-center" data-testid="funnel-empty-state">
      <div className="max-w-2xl mx-auto">
        <div className="inline-flex w-14 h-14 rounded-full grad-bg items-center justify-center mb-4">
          <HelpCircle size={26} className="text-white" />
        </div>
        <h3 className="text-xl font-extrabold mb-2">Como preencher seu funil</h3>
        <p className="text-sm text-muted mb-5">
          Este funil é atualizado <strong>manualmente</strong>, uma vez por mês. Não precisa de integração.
          Colete os dados nos lugares abaixo e digite os volumes de cada etapa:
        </p>
        <div className="grid md:grid-cols-2 gap-3 text-left mb-5">
          {[
            { s: "Alcance", w: "Instagram Insights → Alcance / Facebook Insights → Reach" },
            { s: "Cliques na bio", w: "Linktree, Beacons, ou UTM da bio no Google Analytics" },
            { s: "Acesso à página", w: "GA4 → Aquisição → tráfego de social" },
            { s: "Leads", w: "Seu formulário (RD, HubSpot, planilha)" },
            { s: "MQL", w: "CRM → contatos qualificados por marketing" },
            { s: "Vendas", w: "CRM ou financeiro → propostas fechadas" },
          ].map((x) => (
            <div key={x.s} className="card-2 p-3">
              <div className="text-xs font-bold mb-1">{x.s}</div>
              <div className="text-[11px] text-muted">{x.w}</div>
            </div>
          ))}
        </div>
        <button onClick={onLoadExample} className="btn-grad text-sm flex items-center gap-2 mx-auto" data-testid="load-example-btn">
          <PlayCircle size={14} /> Carregar exemplo com números fictícios
        </button>
        <div className="text-[11px] text-muted mt-3">Você poderá editar tudo depois. É só pra visualizar como fica.</div>
      </div>
    </div>
  );
}

export default function Funnel() {
  const { activeBrand, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [objetivo, setObjetivo] = useState("");
  const debounce = useRef(null);
  const pending = useRef({ vol: {}, dono: {}, meta: {} });

  useEffect(() => {
    if (!activeBrand) return;
    setObjetivo(activeBrand.objetivo_social || "");
    (async () => {
      try {
        const r = await api.get(`/funnel/${activeBrand.brand_id}`);
        setData(r.data);
      } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    })();
    return () => { if (debounce.current) clearTimeout(debounce.current); };
  }, [activeBrand]);

  const flush = async () => {
    const patch = pending.current;
    const body = {};
    for (const s of ["vol", "dono", "meta"]) {
      if (Object.keys(patch[s]).length > 0) body[s] = { ...patch[s] };
    }
    pending.current = { vol: {}, dono: {}, meta: {} };
    if (Object.keys(body).length === 0) return;
    setSaving(true);
    try {
      await api.put(`/funnel/${activeBrand.brand_id}`, body);
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const persist = (section, key, value) => {
    pending.current[section][key] = value;
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(flush, 600);
  };

  const setSectionField = (section, key, value) => {
    setData((d) => ({ ...d, [section]: { ...(d[section] || {}), [key]: value } }));
    persist(section, key, value);
  };

  const saveObjetivo = async (val) => {
    setObjetivo(val);
    if (!val) return;
    try {
      await api.put(`/brands/${activeBrand.brand_id}/objetivo`, { objetivo_social: val });
      toast.success("Objetivo salvo");
      if (refresh) refresh();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  const loadExample = async () => {
    // Flush and clear any pending debounced writes so example values aren't overwritten
    if (debounce.current) { clearTimeout(debounce.current); debounce.current = null; }
    pending.current = { vol: {}, dono: {}, meta: {} };
    setData((d) => ({ ...d, ...EXAMPLE_DATA }));
    setSaving(true);
    try {
      await api.put(`/funnel/${activeBrand.brand_id}`, EXAMPLE_DATA);
      toast.success("Exemplo carregado — edite à vontade");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const analyze = async () => {
    if (!objetivo) {
      toast.error("Escolha primeiro o objetivo da marca em social");
      return;
    }
    setAnalyzing(true);
    try {
      const r = await api.post(`/funnel/${activeBrand.brand_id}/analyze`);
      setData((d) => ({ ...d, diagnostic: r.data }));
      toast.success("Diagnóstico gerado");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setAnalyzing(false); }
  };

  const vol = useMemo(() => (data?.vol || {}), [data]);
  const totalVol = useMemo(() => Object.values(vol).reduce((a, v) => a + Number(v || 0), 0), [vol]);
  const maxVol = Math.max(...STAGES.map((s) => Number(vol[s.key] || 0)), 1);
  const rates = {
    cliquesBio_over_alcance: pct(vol.cliquesBio, vol.alcance),
    acessoPagina_over_cliquesBio: pct(vol.acessoPagina, vol.cliquesBio),
    leads_over_acessoPagina: pct(vol.leads, vol.acessoPagina),
    mql_over_leads: pct(vol.mql, vol.leads),
    vendas_over_mql: pct(vol.vendas, vol.mql),
  };
  const objetivoLabel = OBJETIVOS.find((o) => o.key === objetivo)?.label || "Sem objetivo definido";

  if (!data) return <div className="p-10 text-muted font-mono text-xs">carregando…</div>;

  return (
    <div className="p-6 lg:p-10" data-testid="funnel-page">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold mb-1">// módulo · funil & interpretação</div>
          <h1 className="text-3xl font-extrabold">Funil & Métricas</h1>
          <p className="text-muted text-sm mt-1 max-w-2xl">Você coloca os números. A IA lê os números <strong>à luz do objetivo da sua marca em social</strong> e aponta gargalo + próximos passos.</p>
        </div>
        {saving && <span className="text-xs text-muted flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> salvando…</span>}
      </div>

      {/* Objetivo de marca em social */}
      <div className="card p-5 mb-6" data-testid="objetivo-card">
        <div className="flex items-center gap-2 mb-3">
          <Target size={16} className="grad-text" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted">// passo 1</div>
            <h3 className="font-bold text-base">Qual é o objetivo desta marca em social?</h3>
          </div>
        </div>
        <div className="grid gap-2 md:grid-cols-5">
          {OBJETIVOS.map((o) => (
            <button
              key={o.key}
              onClick={() => saveObjetivo(o.key)}
              className={`text-left p-3 rounded transition-all border ${objetivo === o.key ? "grad-bg text-white border-transparent" : "card-2 hover:border-[var(--accent)]"}`}
              style={{ borderColor: objetivo === o.key ? "transparent" : "var(--border)" }}
              data-testid={`objetivo-${o.key}`}
            >
              <div className="text-xs font-bold mb-1">{o.label.split("—")[0].trim()}</div>
              <div className={`text-[11px] leading-tight ${objetivo === o.key ? "text-white/85" : "text-muted"}`}>{o.hint}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Empty state */}
      {totalVol === 0 && (
        <div className="mb-6">
          <EmptyState onLoadExample={loadExample} />
        </div>
      )}

      {/* Funil visual */}
      <div className="card p-6 mb-6" data-testid="funnel-visual">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted">// passo 2 · volume por etapa</div>
            <h3 className="font-bold">Cadeia de métricas (mês corrente)</h3>
          </div>
          <TrendingUp size={16} className="text-muted" />
        </div>
        <div className="space-y-3">
          {STAGES.map((s, i) => {
            const v = Number(vol[s.key] || 0);
            const w = Math.max((v / maxVol) * 100, 4);
            const rateKey = i === 0 ? null : Object.keys(rates)[i - 1];
            const rate = rateKey ? rates[rateKey] : null;
            return (
              <div key={s.key}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                    <span className="text-sm font-semibold">{s.label}</span>
                    {rate !== null && (
                      <span className="chip text-[10px]" style={{ padding: "2px 6px", color: s.color, borderColor: s.color }}>{fmtRate(rate)}</span>
                    )}
                  </div>
                  <div className="w-32">
                    <InlineNum value={vol[s.key]} onChange={(v2) => setSectionField("vol", s.key, v2)} testid={`vol-${s.key}`} />
                  </div>
                </div>
                <div className="h-4 rounded" style={{ background: "var(--panel-2)" }}>
                  <div className="h-full rounded transition-all" style={{ width: `${w}%`, background: s.color }} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
          {Object.entries(rates).map(([k, v]) => (
            <div key={k} className="card-2 p-3">
              <div className="text-[10px] text-muted font-mono uppercase tracking-widest">{RATE_LABELS[k]}</div>
              <div className="text-xl font-bold mt-1">{fmtRate(v)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabela dono/meta (sem SLA) */}
      <div className="card overflow-hidden" data-testid="funnel-table">
        <div className="px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted">// meta & dono (opcional)</div>
          <div className="text-sm">Defina a meta mensal e quem é o responsável por cada etapa</div>
        </div>
        <div className="grid grid-cols-[1fr_1fr_1fr] gap-0 px-4 py-3 border-b" style={{ background: "var(--panel-2)", borderColor: "var(--border)" }}>
          {["Métrica", "Meta mensal", "Dono (opcional)"].map((h) => <div key={h} className="font-mono text-[10px] uppercase tracking-widest text-muted">{h}</div>)}
        </div>
        {STAGES.map((s) => (
          <div key={s.key} className="grid grid-cols-[1fr_1fr_1fr] gap-2 items-center px-4 py-2 border-b" style={{ borderColor: "var(--border)" }}>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
              <span className="text-sm font-semibold">{s.label}</span>
            </div>
            <InlineNum value={data.meta?.[s.key]} onChange={(v) => setSectionField("meta", s.key, v)} testid={`meta-${s.key}`} />
            <InlineText value={data.dono?.[s.key]} placeholder="ex: Marketing" onChange={(v) => setSectionField("dono", s.key, v)} testid={`dono-${s.key}`} />
          </div>
        ))}
      </div>

      {/* Analisar com IA (passo 3) */}
      <div className="card p-6 mt-6 relative overflow-hidden" data-testid="analyze-hero">
        <div className="absolute inset-0 pointer-events-none opacity-10" style={{ background: "var(--grad-soft)" }} />
        <div className="relative flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold mb-1">// passo 3 · IA</div>
            <h3 className="font-bold text-lg">Analisar funil com IA</h3>
            <p className="text-xs text-muted mt-1 max-w-lg">
              A IA cruza seus números com o objetivo da marca e o posicionamento para gerar: gargalo, leitura estratégica e 3 recomendações.
              {!objetivo && <span className="block mt-1 text-[var(--warning)]">Escolha o objetivo (Passo 1) primeiro.</span>}
              {objetivo && totalVol === 0 && <span className="block mt-1 text-[var(--warning)]">Preencha ao menos um volume (Passo 2) para habilitar.</span>}
            </p>
          </div>
          <button
            onClick={analyze}
            disabled={analyzing || totalVol === 0 || !objetivo}
            className="btn-grad text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="analyze-btn"
          >
            {analyzing ? <><Loader2 size={14} className="animate-spin" /> Analisando…</> : <><Sparkles size={14} /> Analisar com IA</>}
          </button>
        </div>
        {data.diagnostic && (
          <div className="relative mt-5">
            <div className="text-[10px] text-muted font-mono uppercase tracking-widest mb-2">// último diagnóstico · {new Date(data.diagnostic.generated_at).toLocaleString("pt-BR")}</div>
            <DiagnosticCard diagnostic={data.diagnostic} objetivoLabel={objetivoLabel} />
          </div>
        )}
      </div>
    </div>
  );
}
